export type InstanceDisplayState =
  | 'launching'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'terminating'
  | 'terminated'
  | 'error';

export interface StatusVisuals {
  displayState: InstanceDisplayState;
  label: string;
  color: string;
  isAnimated: boolean;
  gradientClass: string;
  badgeClasses: string;
  dotColor: string;
}

const STATUS_VISUALS: Record<InstanceDisplayState, StatusVisuals> = {
  launching: {
    displayState: 'launching',
    label: 'Launching...',
    color: 'yellow',
    isAnimated: true,
    gradientClass: 'via-yellow-500/40',
    badgeClasses: 'bg-yellow-500/20 text-yellow-400',
    dotColor: 'bg-yellow-500',
  },
  starting: {
    displayState: 'starting',
    label: 'Starting...',
    color: 'blue',
    isAnimated: true,
    gradientClass: 'via-blue-500/40',
    badgeClasses: 'bg-blue-500/20 text-blue-400',
    dotColor: 'bg-blue-500',
  },
  running: {
    displayState: 'running',
    label: 'Running',
    color: 'green',
    isAnimated: false,
    gradientClass: '',
    badgeClasses: 'bg-green-500/20 text-green-400',
    dotColor: 'bg-green-500',
  },
  stopping: {
    displayState: 'stopping',
    label: 'Stopping...',
    color: 'gray',
    isAnimated: true,
    gradientClass: 'via-gray-500/40',
    badgeClasses: 'bg-gray-500/20 text-gray-400',
    dotColor: 'bg-gray-500',
  },
  stopped: {
    displayState: 'stopped',
    label: 'Stopped',
    color: 'gray',
    isAnimated: false,
    gradientClass: '',
    badgeClasses: 'bg-gray-500/20 text-gray-400',
    dotColor: 'bg-gray-500',
  },
  terminating: {
    displayState: 'terminating',
    label: 'Terminating...',
    color: 'red',
    isAnimated: true,
    gradientClass: 'via-red-500/40',
    badgeClasses: 'bg-red-500/20 text-red-400',
    dotColor: 'bg-red-500',
  },
  terminated: {
    displayState: 'terminated',
    label: 'Terminated',
    color: 'red',
    isAnimated: false,
    gradientClass: '',
    badgeClasses: 'bg-red-500/20 text-red-400',
    dotColor: 'bg-red-500',
  },
  error: {
    displayState: 'error',
    label: 'Error',
    color: 'red',
    isAnimated: false,
    gradientClass: '',
    badgeClasses: 'bg-red-500/20 text-red-400',
    dotColor: 'bg-red-500',
  },
};

export function getInstanceDisplayState(
  status: string,
  terminalReady?: boolean | null
): InstanceDisplayState {
  const s = status.toLowerCase();

  if (s === 'launching' || s === 'pending') return 'launching';
  if (s === 'stopping') return 'stopping';
  if (s === 'stopped') return 'stopped';
  if (s === 'terminating') return 'terminating';
  if (s === 'terminated') return 'terminated';
  if (s === 'error' || s === 'launch_failed') return 'error';
  if (s === 'running') {
    return terminalReady === true ? 'running' : 'starting';
  }

  return 'error';
}

export function getStatusVisuals(displayState: InstanceDisplayState): StatusVisuals {
  return STATUS_VISUALS[displayState];
}

export function getInstanceSummaryText(
  instances: Array<{ status: string; terminal_ready?: boolean | null }>
): string | undefined {
  if (instances.length === 0) return undefined;

  const counts: Partial<Record<InstanceDisplayState, number>> = {};
  for (const inst of instances) {
    const state = getInstanceDisplayState(inst.status, inst.terminal_ready);
    counts[state] = (counts[state] || 0) + 1;
  }

  // Build summary parts in priority order
  const parts: string[] = [];
  const order: InstanceDisplayState[] = ['running', 'starting', 'launching', 'terminating', 'stopping', 'stopped', 'error', 'terminated'];
  for (const state of order) {
    const count = counts[state];
    if (count && count > 0) {
      parts.push(`${count} ${state}`);
    }
  }

  return parts.length > 0 ? parts.join(', ') : undefined;
}

/** Get the badge circle color class based on active instances */
export function getInstanceBadgeColor(
  instances: Array<{ status: string; terminal_ready?: boolean | null }>
): string {
  let hasRunningOrStarting = false;
  let hasLaunching = false;
  let hasTerminating = false;

  for (const inst of instances) {
    const state = getInstanceDisplayState(inst.status, inst.terminal_ready);
    if (state === 'running' || state === 'starting') hasRunningOrStarting = true;
    else if (state === 'launching') hasLaunching = true;
    else if (state === 'terminating') hasTerminating = true;
  }

  if (hasRunningOrStarting) return 'bg-green-500';
  if (hasLaunching) return 'bg-yellow-500';
  if (hasTerminating) return 'bg-red-500';
  return 'bg-green-500';
}
